/**
 * PodSsoConsumePage — Public route /admin/sso
 *
 * Reads ?token=...&view_mode=... from the URL, calls
 * POST /api/agency/sso/consume to exchange the magic link for a Cockpit
 * admin JWT (+ a 3-min update_token), stores both in localStorage, then
 * redirects to /admin/dashboard.
 *
 * No auth wrapper around it — this IS the auth entry point.
 */
import React, { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { Loader2, CheckCircle2, XCircle, ShieldCheck } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PodSsoConsumePage() {
  const [params] = useSearchParams();
  const nav = useNavigate();
  const [state, setState] = useState({ status: "loading", message: "Vérification du magic link…", email: "" });

  useEffect(() => {
    const token = params.get("token");
    const viewMode = params.get("view_mode") || "";
    if (!token) {
      setState({ status: "error", message: "Token manquant dans l'URL." });
      return;
    }

    axios.post(`${API}/agency/sso/consume`, { token })
      .then(r => {
        const d = r.data || {};
        if (!d.token) throw new Error("Réponse sans token");
        // Store admin JWT
        localStorage.setItem("token", d.token);
        if (d.user) {
          localStorage.setItem("user", JSON.stringify(d.user));
        }
        // Store the short-lived update_token separately (used by the self-update flow)
        if (d.update_token) {
          sessionStorage.setItem("cockpit_update_token", d.update_token);
        }
        if (viewMode) {
          sessionStorage.setItem("cockpit_view_mode", viewMode);
        }
        setState({ status: "ok", message: "Connexion réussie", email: d.user?.email || "" });
        // Small delay so the user sees the success screen
        setTimeout(() => nav("/admin/dashboard", { replace: true }), 800);
      })
      .catch(err => {
        const detail = err?.response?.data?.detail || err.message || "Erreur inconnue";
        setState({ status: "error", message: `Échec : ${detail}` });
      });
  }, [params, nav]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 p-6" data-testid="sso-consume-page">
      <div className="bg-white rounded-2xl shadow-xl p-10 max-w-md w-full text-center">
        <ShieldCheck className="w-12 h-12 mx-auto mb-4 text-emerald-500" />
        <h1 className="font-display text-2xl font-black text-slate-900 mb-2">Connexion via le HUB Agence</h1>
        <p className="text-sm text-slate-500 mb-8">Lien à usage unique · 3 minutes</p>

        {state.status === "loading" && (
          <div className="text-slate-700">
            <Loader2 className="w-7 h-7 mx-auto animate-spin mb-3 text-slate-400" />
            <p>{state.message}</p>
          </div>
        )}

        {state.status === "ok" && (
          <div className="text-emerald-600">
            <CheckCircle2 className="w-7 h-7 mx-auto mb-3" />
            <p className="font-bold">{state.message}</p>
            {state.email && <p className="text-xs text-slate-500 mt-1">Connecté en tant que {state.email}</p>}
            <p className="text-xs text-slate-400 mt-3">Redirection vers le dashboard…</p>
          </div>
        )}

        {state.status === "error" && (
          <div className="text-red-600">
            <XCircle className="w-7 h-7 mx-auto mb-3" />
            <p className="font-semibold">{state.message}</p>
            <button
              onClick={() => nav("/admin/login", { replace: true })}
              className="mt-6 px-5 py-2.5 rounded-full bg-slate-900 text-white text-sm font-bold"
              data-testid="sso-go-login"
            >
              Aller à la connexion
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
